import type { ReactNode } from 'react'
import type { PowerKind, PublicView } from '@sh/shared'
import {
  ChancellorIcon,
  DoveIcon,
  ExecutedIcon,
  OfflineIcon,
  PowerIcon,
  PresidentIcon,
  ReadyIcon,
  TermLimitedIcon,
  VetoIcon,
  VictoryIcon,
} from './Icons.js'
import { Mark } from './Tooltip.js'
import { useFlash } from '../net/useGameEvents.js'
import { useAsset } from '../skin/SkinContext.js'

/** Short enough to sit inside a 46px tile on a phone. Also the rules page's headings. */
export const POWER_LABEL: Record<PowerKind, string> = {
  investigate: 'Investigate',
  special_election: 'Special election',
  policy_peek: 'Policy peek',
  execution: 'Execution',
}

/**
 * The Hitler-as-Chancellor win arms once three fascist policies are down, so
 * from the fourth slot on electing him ends the game. That is the single most
 * expensive thing a table can forget, and the physical board prints it under
 * exactly these slots.
 */
const DANGER_FROM = 3

/**
 * The fifth fascist policy hands the government the veto. Unlike the danger
 * band it is not printed under a slot on the physical board, only listed in the
 * rules, which is exactly why a table forgets it exists until someone tries.
 */
const VETO_FROM = 5

/**
 * A policy tile. Colour is never the only signal: each tile carries its initial
 * and a distinct fill pattern, so the board survives colour blindness and the
 * dim lighting people actually play in.
 *
 * A skin may supply artwork, which is layered over the tile rather than
 * replacing it. The letter is what carries the tile when there is no artwork;
 * where there is, the card already says LIBERAL or FASCIST across its own face
 * and a chip in the corner repeating it was just clutter on top of the art.
 *
 * An empty slot is not blank. Where the track grants a power, the empty tile
 * carries that power as an icon and a label, so the board answers "what happens
 * if this goes fascist" without anybody having to remember the table. The slot
 * that ends each track says so the same way - the eagle on the sixth fascist
 * slot, the dove on the fifth liberal one - so both finish lines are on the
 * board from the first round rather than only the one the powers made visible.
 */
export function PolicyTile({
  policy,
  filled = true,
  size = 'md',
  power,
  vetoHere = false,
  victory = false,
  fresh = false,
}: {
  policy: 'liberal' | 'fascist'
  filled?: boolean
  size?: 'sm' | 'md' | 'lg'
  /** Shown inside the tile while the slot is still empty. */
  power?: PowerKind | null
  /**
   * This slot also puts the veto in the government's hands. It is the same
   * fifth fascist policy as the second Execution, and the band at the foot of
   * the panel was the only thing saying so - which meant a table reading the
   * track saw "Execution", planned around an execution, and was surprised by a
   * veto in the same breath.
   */
  vetoHere?: boolean
  /**
   * The slot that ends the game rather than granting a power: the sixth
   * fascist policy, or the fifth liberal one. Which side it hands the game to
   * follows from `policy`.
   */
  victory?: boolean
  /** Just enacted - animates in. */
  fresh?: boolean
}) {
  const art = useAsset(`tile.${policy}`)
  return (
    <span
      className={`tile tile--${policy} tile--${size} ${filled ? 'is-filled' : 'is-empty'} ${
        filled && art ? 'has-art' : ''
      } ${fresh ? 'is-fresh' : ''}`}
      role="img"
      aria-label={
        filled
          ? `${policy} policy`
          : power
            ? `empty slot, grants ${POWER_LABEL[power]}${vetoHere ? ' and unlocks Veto' : ''}`
            : victory
              ? `empty slot, ${policy === 'liberal' ? 'liberals' : 'fascists'} win`
              : 'empty slot'
      }
    >
      {filled && art && <img className="tile__art" src={art} alt="" aria-hidden="true" draggable={false} />}
      {filled && !art && (
        <span className="tile__letter">{policy === 'liberal' ? 'L' : 'F'}</span>
      )}
      {!filled && (power || victory) && (
        <span className="tile__power" aria-hidden="true">
          {power ? <PowerIcon kind={power} /> : policy === 'liberal' ? <DoveIcon /> : <VictoryIcon />}
          {/* The break is written rather than left to the wrap, so the "+" leads
              the second line instead of trailing the first. */}
          <span className="tile__power-label">
            {power ? POWER_LABEL[power] : policy === 'liberal' ? 'Liberals win' : 'Fascists win'}
            {power && vetoHere && (
              <>
                <br />+ Veto
              </>
            )}
          </span>
        </span>
      )}
    </span>
  )
}

function Slot({
  policy,
  index,
  enacted,
  power,
  vetoHere,
  victory,
  fresh,
}: {
  policy: 'liberal' | 'fascist'
  index: number
  enacted: number
  power?: PowerKind | null
  vetoHere?: boolean
  victory?: boolean
  fresh: boolean
}) {
  const filled = index < enacted
  return (
    <div className="track__slot">
      <PolicyTile
        policy={policy}
        filled={filled}
        power={filled ? null : power}
        vetoHere={!filled && vetoHere}
        victory={!filled && victory}
        fresh={fresh && index === enacted - 1}
      />
    </div>
  )
}

/**
 * The veto band, which is always here. It used to be a badge that appeared
 * beside the deck counts on the fifth fascist policy, which meant the one
 * moment it had something to say was also the moment it shoved the row it
 * lived in sideways. Now it is the fascist panel's footer in both states: a
 * dim line counting down to the fifth policy, then a lit one. Nothing moves,
 * and the rule is on the board before it matters rather than after.
 */
function VetoBand({ unlocked, enacted }: { unlocked: boolean; enacted: number }) {
  const arriving = useFlash(unlocked, 2400)
  const remaining = Math.max(0, VETO_FROM - enacted)
  return (
    <div
      className={`veto ${unlocked ? 'is-live' : ''} ${unlocked && arriving ? 'is-fresh' : ''}`}
      role="status"
    >
      <span className="veto__icon" aria-hidden="true">
        <VetoIcon />
      </span>
      <span className="veto__text">
        {unlocked ? (
          <>
            <strong>Veto power in effect</strong> — the government may kill an agenda outright
          </>
        ) : (
          <>
            Veto power unlocks on the {VETO_FROM}th fascist policy
            {remaining > 0 && ` — ${remaining} to go`}
          </>
        )}
      </span>
    </div>
  )
}

/**
 * One track, framed. Both tracks are recessed panels rather than a bare row of
 * cards, so the tiles read as policies laid into a board instead of floating on
 * the page - and so the fascist track has somewhere to put the two rules it has
 * to carry. Those stack under its slots: the Hitler warning pinned beneath the
 * three slots it applies to, then the veto band across the foot.
 *
 * The panel is neutral, not party-tinted. The danger band behind the last three
 * fascist slots is the only red region on the board, and a red panel behind it
 * would have cost it the contrast that is the entire point of it.
 */
function Track({
  policy,
  enacted,
  size,
  powers,
  vetoUnlocked = false,
}: {
  policy: 'liberal' | 'fascist'
  enacted: number
  size: number
  powers?: (PowerKind | null)[]
  /** Fascist track only: draws the veto band lit. */
  vetoUnlocked?: boolean
}) {
  // Held past the end of the landing animation, or the class is pulled out from
  // under it and the glow is cut off mid-pulse.
  const fresh = useFlash(enacted, 1900)
  const indices = Array.from({ length: size }, (_, i) => i)
  const slot = (i: number) => (
    <Slot
      key={i}
      policy={policy}
      index={i}
      enacted={enacted}
      power={powers?.[i] ?? null}
      // Guarded on the slot actually granting something: if the track table
      // ever moves, a slot with no power says nothing rather than "+ Veto".
      vetoHere={policy === 'fascist' && i === VETO_FROM - 1 && Boolean(powers?.[i])}
      victory={i === size - 1}
      fresh={fresh}
    />
  )

  const armed = policy === 'fascist' && enacted >= DANGER_FROM
  return (
    <section className={`track track--${policy} ${armed ? 'is-armed' : ''}`}>
      <header className="track__head">
        <h3 className="track__label">{policy === 'liberal' ? 'Liberal' : 'Fascist'}</h3>
        <span className="track__count" aria-label={`${enacted} of ${size} enacted`}>
          <strong>{enacted}</strong>
          <span aria-hidden="true">/{size}</span>
        </span>
      </header>
      <div
        className={`track__slots ${armed ? 'is-armed' : ''} ${
          fresh && enacted === DANGER_FROM ? 'is-arming' : ''
        }`}
      >
        {indices.map(slot)}
        {policy === 'fascist' && (
          <span className="track__danger-label">
            From here, Hitler as Chancellor ends the game
          </span>
        )}
      </div>
      {policy === 'fascist' && <VetoBand unlocked={vetoUnlocked} enacted={enacted} />}
    </section>
  )
}

export function ElectionTracker({ value }: { value: number }) {
  const fresh = useFlash(value)
  return (
    <div className="tracker" aria-label={`election tracker at ${value} of 3`}>
      <span className="tracker__label">Failed elections</span>
      <span className="tracker__dots">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`tracker__dot ${i < value ? 'is-on' : ''} ${
              fresh && i === value - 1 ? 'is-fresh' : ''
            }`}
          />
        ))}
      </span>
      {value === 2 && <span className="tracker__warning">one more throws the country into chaos</span>}
    </div>
  )
}

function Announcements({ view }: { view: PublicView }) {
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? 'someone'
  if (view.announcements.length === 0) return null
  return (
    <ul className="announcements">
      {view.announcements.map((a, i) => (
        <li key={i} className="announcements__item">
          {a.kind === 'chaos_policy' && `Three failed elections: the people enacted a ${a.policy} policy.`}
          {a.kind === 'policy_enacted' && `A ${a.policy} policy was enacted.`}
          {a.kind === 'deck_reshuffled' && 'The discard pile was shuffled back into the deck.'}
          {a.kind === 'veto_enacted' && 'The government vetoed the agenda.'}
          {a.kind === 'veto_rejected' && 'The President refused the veto.'}
          {a.kind === 'power_skipped' && `Power skipped: ${a.reason}.`}
          {a.kind === 'player_executed' && `${name(a.targetId)} was executed.`}
          {a.kind === 'player_investigated' && `${name(a.targetId)} was investigated.`}
          {a.kind === 'special_election' && `${name(a.targetId)} was handed the Presidency.`}
        </li>
      ))}
    </ul>
  )
}

/**
 * How the last government's vote went, as the ballots themselves: every seat's
 * Ja or Nein turned face up, in seat order, the way the physical game ends a
 * vote with the whole table flipping its card at once.
 *
 * It used to be a sentence and two comma-separated lists of names. Accurate,
 * and read by nobody past arm's length - and the question the table is asking
 * is not how many, it is who. A row of coloured cards answers that by colour
 * from across a room, and the one who broke ranks is a gap in the row.
 */
export function LastVote({ view }: { view: PublicView }) {
  const vote = view.lastVote
  const fresh = useFlash(vote ? `${vote.presidentId}:${vote.chancellorId}:${vote.ja.length}` : null, 2000)
  if (!vote) return null
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  // Seat order rather than the order ballots landed: it is the order the table
  // is sitting in, so a card can be matched to a face without reading it.
  const voters = view.seatOrder.filter((id) => vote.ja.includes(id) || vote.nein.includes(id))
  return (
    <div
      className={`lastvote ${vote.passed ? 'is-passed' : 'is-failed'} ${fresh ? 'is-fresh' : ''}`}
      data-testid="last-vote"
    >
      <div className="lastvote__head">
        <span className="lastvote__gov">
          {name(vote.presidentId)} &amp; {name(vote.chancellorId)}
        </span>
        <span className="lastvote__verdict">
          {vote.passed ? 'Elected' : 'Rejected'} {vote.ja.length}&ndash;{vote.nein.length}
        </span>
      </div>
      <ul className="lastvote__ballots">
        {voters.map((id) => {
          const ja = vote.ja.includes(id)
          return (
            <li key={id} className={`lastvote__ballot ${ja ? 'is-ja' : 'is-nein'}`}>
              <span className="lastvote__card">{ja ? 'Ja!' : 'Nein'}</span>
              <span className="lastvote__name">{name(id)}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * The draw and discard piles as piles, looked down on from above. "deck 11 ·
 * discard 3" was accurate and invisible; a card counter matters most in the
 * last rounds before a reshuffle, and nobody reads a caption for that.
 *
 * The stack is drawn from the count, capped, so a thick pile looks thick. The
 * faces stay blank: what is in either pile is never revealed to anybody.
 */
function Deck({ count, label, kind }: { count: number; label: string; kind: 'draw' | 'discard' }) {
  const layers = Math.min(count, 7)
  return (
    <div className={`deck deck--${kind}`} aria-label={`${label}: ${count} policies`}>
      <span className="deck__stack">
        {Array.from({ length: layers }, (_, i) => (
          <span key={i} className="deck__layer" style={{ ['--i' as string]: layers - 1 - i }} />
        ))}
        <span className="deck__top">
          <span className="deck__count">{count}</span>
        </span>
      </span>
      <span className="deck__label">{label}</span>
    </div>
  )
}

/**
 * One row of the seating list, so it can animate its own state changes.
 *
 * The states that used to be spelled out are marks now. "executed",
 * "term-limited" and "offline" are each longer than most of the names they sat
 * beside, and a seat holding two of them wrapped onto a third line; a row of
 * small glyphs says the same thing in the width of a name. Every one carries
 * its old word as its accessible name and in a bubble on hover or focus, so
 * nothing was traded away for the space - see Mark.
 *
 * The sitting government is not a badge at all. A President and a Chancellor
 * are the two facts the table checks most often, and hunting two small chips
 * down a list of ten was the slowest thing on this screen, so those two rows
 * are simply louder than the rest.
 */
function Seat({
  view,
  id,
  index,
  termLimited,
}: {
  view: PublicView
  id: string
  index: number
  termLimited: boolean
}) {
  const player = view.players.find((p) => p.id === id)
  const dying = useFlash(player?.alive ?? true, 2200)
  const crowned = useFlash(view.presidentId === id ? 'pres' : 'none', 2200)
  if (!player) return null
  const role = view.roles?.[id]
  const isPresident = id === view.presidentId
  const isChancellor = id === view.chancellorId
  const inGovernment = isPresident || isChancellor

  return (
    <li
      className={`seat ${player.alive ? '' : 'is-dead'} ${
        view.waitingOn.includes(id) ? 'is-waiting' : ''
      } ${dying && !player.alive ? 'is-dying' : ''} ${
        crowned && isPresident ? 'is-crowned' : ''
      } ${inGovernment ? 'is-gov' : ''} ${isPresident ? 'is-pres' : ''} ${
        isChancellor ? 'is-chan' : ''
      }`}
      data-testid={`seat-${player.name}`}
    >
      <span className="seat__index">{index + 1}</span>
      <span className="seat__name">{player.name}</span>
      <span className="seat__marks">
        {isPresident && (
          <Mark label="President" className="mark--pres">
            <PresidentIcon />
          </Mark>
        )}
        {isChancellor && (
          <Mark label="Chancellor" className="mark--chan">
            <ChancellorIcon />
          </Mark>
        )}
        {!player.alive && (
          <Mark label="Executed" className="mark--dead">
            <ExecutedIcon />
          </Mark>
        )}
        {player.alive && termLimited && (
          <Mark label="Term-limited" className="mark--term">
            <TermLimitedIcon />
          </Mark>
        )}
        {!player.connected && (
          <Mark label="Offline" className="mark--off">
            <OfflineIcon />
          </Mark>
        )}
        {view.phase === 'voting' && view.ballotsCast.includes(id) && (
          <Mark label="Ballot cast" className="mark--ready">
            <ReadyIcon />
          </Mark>
        )}
        {view.phase === 'role_reveal' && view.revealAck.includes(id) && (
          <Mark label="Role acknowledged" className="mark--ready">
            <ReadyIcon />
          </Mark>
        )}
      </span>
      <span className="seat__badges">
        {id === view.nomineeId && <span className="badge badge--nom">nominated</span>}
        {role && <span className={`badge badge--role badge--${role}`}>{role}</span>}
      </span>
      {view.waitingOn.includes(id) && (
        <span className="seat__waiting" aria-label="waiting on this player">
          <i />
          <i />
          <i />
        </span>
      )}
    </li>
  )
}

/**
 * Current state and enforcement badges. Deliberately not a log: there is no
 * scrollback, only what a physical table would show right now.
 *
 * Two regions, and the split is what lets one component serve a phone and a
 * television. `board__main` is the board proper - both tracks and the counters
 * printed beside them. `board__side` is everything about the people: the seats,
 * what just happened, and how the last vote went. On a phone they stack in that
 * order. On a table screen CSS puts them side by side, which is the only way
 * the tracks get the full width of the room's biggest screen.
 *
 * The tracks themselves are stacked now rather than dealt out in two columns.
 * Side by side, six slots had to fit in half the width; stacked, they get all
 * of it, and the board is the thing this screen exists to show.
 *
 * `secret` is the one thing here a table screen never passes: your own role,
 * behind its hold, at the foot of the list of people. It is a row about a
 * player among the rows about players, which is where somebody looks for it -
 * and a seat list is exactly the thing you are reading when the question
 * "which of these am I with" comes up.
 */
export function Board({ view, secret }: { view: PublicView; secret?: ReactNode }) {
  const fiveOrFewer = view.players.filter((p) => p.alive).length <= 5
  const termLimited = (id: string) =>
    id === view.lastElectedChancellorId || (!fiveOrFewer && id === view.lastElectedPresidentId)

  return (
    <div className="board">
      <div className="board__main">
        <div className="board__tracks">
          <Track policy="liberal" enacted={view.liberalPolicies} size={5} />
          <Track
            policy="fascist"
            enacted={view.fascistPolicies}
            size={6}
            powers={view.fascistTrack}
            vetoUnlocked={view.vetoUnlocked}
          />
        </div>

        <div className="board__meta">
          <ElectionTracker value={view.electionTracker} />
          <div className="board__deck">
            <Deck kind="draw" count={view.deckSize} label="Deck" />
            <Deck kind="discard" count={view.discardSize} label="Discard" />
          </div>
        </div>
      </div>

      <div className="board__side">
        <ul className="seats">
          {view.seatOrder.map((id, index) => (
            <Seat key={id} view={view} id={id} index={index} termLimited={termLimited(id)} />
          ))}
        </ul>

        {secret}

        <Announcements view={view} />
        <LastVote view={view} />
      </div>
    </div>
  )
}
